import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, Github } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface ProjectCardProps {
  id: string;
  title: string;
  description: string;
  image: string;
  tags: string[];
  githubUrl?: string;
  liveUrl?: string;
  featured?: boolean;
  className?: string;
  completionDate?: string;
  contextLabel?: string;
}

// Helper to parse 'YYYY-MM-DD' as local date
function parseLocalDate(dateString: string) {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const ProjectCard = ({
  id,
  title,
  description,
  image,
  tags,
  githubUrl,
  liveUrl,
  featured = false,
  className,
  completionDate,
  contextLabel,
}: ProjectCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [allowPointerTilt, setAllowPointerTilt] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = () => setAllowPointerTilt(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    setImageLoaded(false);
    const img = new Image();
    const done = () => setImageLoaded(true);
    img.onload = done;
    img.onerror = done;
    img.src = image;
    if (img.complete) done();
    const t = window.setTimeout(done, 4000);
    return () => {
      clearTimeout(t);
      img.onload = null;
      img.onerror = null;
    };
  }, [image]);

  const handleNavigate = () => {
    navigate(`/project/${id}`);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!allowPointerTilt || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const tiltX = (y - centerY) / centerY * -8;
    const tiltY = (x - centerX) / centerX * 8;
    setTilt({ x: tiltX, y: tiltY });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  // Function to handle external link clicks
  const handleExternalClick = (
    e: React.MouseEvent<HTMLButtonElement>, 
    url?: string
  ) => {
    if (!url) return;
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const tiltStyle =
    allowPointerTilt && (tilt.x !== 0 || tilt.y !== 0)
      ? {
          transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: 'transform 0.1s ease-out',
        }
      : undefined;

  return (
    <motion.div
      ref={cardRef}
      className={cn(
        'glass-card group min-w-0 cursor-pointer overflow-hidden rounded-lg',
        'max-md:bg-card/95 max-md:backdrop-blur-none dark:max-md:bg-card/95',
        featured ? 'md:col-span-2' : '',
        className
      )}
      initial="rest"
      whileHover="hover"
      variants={{
        rest: { scale: 1 },
        hover: { scale: 1.02 },
      }}
      style={tiltStyle}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleNavigate}
    >
      <div className="flex h-full min-w-0 flex-col">
        <div className="relative aspect-video w-full min-h-0 overflow-hidden">
          <div className={cn(
            "absolute inset-0 bg-muted/20 flex items-center justify-center transition-opacity duration-500",
            imageLoaded ? "opacity-0" : "opacity-100"
          )}>
            <span className="text-muted-foreground">Loading...</span>
          </div>
          <motion.img
            src={image}
            alt={title}
            className={cn(
              "w-full h-full object-cover transition-all duration-500",
              imageLoaded ? "opacity-100" : "opacity-0",
              "group-hover:scale-105"
            )}
            onLoad={() => setImageLoaded(true)}
            variants={{
              rest: { scale: 1 },
              hover: { scale: 1.05 }
            }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <div className="p-6 flex flex-col flex-grow">
          <div className="flex items-start justify-between mb-2 gap-2">
            <div>
              <h3 className="text-xl font-semibold">{title}</h3>
              {completionDate && (
                <span className="block text-xs text-muted-foreground mt-1">
                  {parseLocalDate(completionDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              {contextLabel && (
                <Badge variant="outline" className="text-xs font-normal">
                  {contextLabel}
                </Badge>
              )}
              {featured && (
                <Badge variant="secondary">
                  Featured
                </Badge>
              )}
            </div>
          </div>
          <p className="text-muted-foreground mb-4 line-clamp-3">{description}</p>
          <div className="mt-auto">
            <div className="flex flex-wrap gap-2 mb-4">
              {tags.map((tag) => (
                <Badge key={tag} variant="outline" className="bg-background/50">
                  {tag}
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              {githubUrl && (
                <Button
                  variant="outline" 
                  size="sm"
                  className="gap-1"
                  onClick={(e) => handleExternalClick(e, githubUrl)}
                >
                  <Github className="h-4 w-4" />
                  <span>Code</span>
                </Button>
              )}
              {liveUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={(e) => handleExternalClick(e, liveUrl)}
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Live</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectCard;
